import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  LoanApplication,
  CompanyInformation,
  OwnerInformation,
  DisclosureQuestions,
  BuilderExperience,
  CompanyHistory,
  CreditReference,
  CreditReferencesData,
  SupplierReference,
  ProjectInformation,
  AcknowledgementData,
  AcknowledgementSignature,
  RecentlyBuiltHome,
  BorrowerType,
  BuildType,
} from '../types/application.types';
import {
  initialLoanApplication,
  initialCompanyHistory,
  initialRecentlyBuiltHome,
  initialAcknowledgementSignature,
} from '../types/application.types';

interface ApplicationState extends LoanApplication {
  // Actions for borrower type selection
  setBorrowerType: (type: BorrowerType) => void;
  setBuildType: (type: BuildType) => void;
  setBuiltFiveIn3Years: (value: boolean) => void;
  setNewBorrowerNoReason: (reason: string) => void;
  setExperiencedDetail: (detail: string) => void;

  // Actions for Company Information (Page 1)
  setCompanyInformation: (data: Partial<CompanyInformation>) => void;

  // Actions for Company Ownership (Page 2)
  setIsOwnedByCompany: (value: boolean) => void;
  setParentCompanyName: (name: string) => void;

  // Actions for Ownership Information (Page 2)
  setOwners: (owners: OwnerInformation[]) => void;
  addOwner: () => void;
  removeOwner: (index: number) => void;
  updateOwner: (index: number, data: Partial<OwnerInformation>) => void;

  // Actions for Disclosure Questions (Page 3)
  setDisclosureQuestions: (data: Partial<DisclosureQuestions>) => void;

  // Actions for Builder Experience (Page 4)
  setBuilderExperience: (data: Partial<BuilderExperience>) => void;
  addCompanyHistory: () => void;
  removeCompanyHistory: (index: number) => void;
  updateCompanyHistory: (index: number, data: Partial<CompanyHistory>) => void;

  // Actions for Credit References (Page 5)
  addCreditReference: () => void;
  removeCreditReference: (index: number) => void;
  updateCreditReference: (index: number, data: Partial<CreditReference>) => void;
  setCreditReferencesData: (data: Partial<CreditReferencesData>) => void;
  updateSupplierReference: (supplierType: keyof CreditReferencesData, data: Partial<SupplierReference>) => void;

  // Actions for Project Information (Page 6)
  setProjectInformation: (data: Partial<ProjectInformation>) => void;

  // Actions for Acknowledgement (Page 7)
  setAcknowledgement: (data: Partial<AcknowledgementData>) => void;
  addSignature: () => void;
  removeSignature: (index: number) => void;
  updateSignature: (index: number, data: Partial<AcknowledgementSignature>) => void;

  // Actions for Recently Built Homes (Instructions page)
  addRecentlyBuiltHome: () => void;
  removeRecentlyBuiltHome: (index: number) => void;
  updateRecentlyBuiltHome: (index: number, data: Partial<RecentlyBuiltHome>) => void;

  // Application ID action
  setApplicationId: (id: string | null) => void;

  // Navigation actions
  setCurrentPage: (page: number) => void;
  markPageCompleted: (page: number) => void;
  navigateToPage: (page: number) => void;

  // File upload actions
  setFiles: (type: keyof LoanApplication, files: File[]) => void;

  // Reset application
  resetApplication: () => void;
}

export const APPLICATION_STORAGE_KEY = 'loan-application-storage';

export const useApplicationStore = create<ApplicationState>()(
  persist(
    (set, get) => ({
      ...initialLoanApplication,

      // Borrower type actions
      setBorrowerType: (type) => set({ borrowerType: type }),
      setBuildType: (type) => set({ buildType: type }),
      setBuiltFiveIn3Years: (value) => set({ builtFiveIn3Years: value }),
      setNewBorrowerNoReason: (reason) => set({ newBorrowerNoReason: reason }),
      setExperiencedDetail: (detail) => set({ experiencedDetail: detail }),

      // Company Information actions
      setCompanyInformation: (data) =>
        set((state) => ({
          companyInformation: {
            ...state.companyInformation,
            ...data,
          },
        })),

      // Company Ownership actions
      setIsOwnedByCompany: (value) => set({ isOwnedByCompany: value }),
      setParentCompanyName: (name) => set({ parentCompanyName: name }),

      // Ownership Information actions
      setOwners: (owners) => set({ owners }),
      addOwner: () => {
        const currentOwners = get().owners;
        const newOwner: OwnerInformation = {
          fullName: '',
          title: '',
          ownershipPercent: 0,
          ssn: '',
          dateOfBirth: '',
          phone: '',
          email: '',
          address1: '',
          city: '',
          state: '',
          zip: '',
          isGuarantor: false,
        };
        set({ owners: [...currentOwners, newOwner] });
      },

      removeOwner: (index) => {
        const currentOwners = get().owners;
        if (currentOwners.length > 1) {
          set({
            owners: currentOwners.filter((_, i) => i !== index),
          });
        }
      },

      updateOwner: (index, data) =>
        set((state) => ({
          owners: state.owners.map((owner, i) =>
            i === index ? { ...owner, ...data } : owner
          ),
        })),

      // Disclosure Questions actions
      setDisclosureQuestions: (data) =>
        set((state) => ({
          disclosureQuestions: {
            ...state.disclosureQuestions,
            ...data,
          },
        })),

      // Builder Experience actions
      setBuilderExperience: (data) =>
        set((state) => ({
          builderExperience: {
            ...state.builderExperience,
            ...data,
          },
        })),

      addCompanyHistory: () => {
        const currentHistory = get().builderExperience.companyHistory || [];
        const newCompany: CompanyHistory = { ...initialCompanyHistory };
        set({
          builderExperience: {
            ...get().builderExperience,
            companyHistory: [...currentHistory, newCompany],
          },
        });
      },

      removeCompanyHistory: (index) => {
        const currentHistory = get().builderExperience.companyHistory || [];
        set({
          builderExperience: {
            ...get().builderExperience,
            companyHistory: currentHistory.filter((_, i) => i !== index),
          },
        });
      },

      updateCompanyHistory: (index, data) =>
        set((state) => ({
          builderExperience: {
            ...state.builderExperience,
            companyHistory: (state.builderExperience.companyHistory || []).map((company, i) =>
              i === index ? { ...company, ...data } : company
            ),
          },
        })),

      // Credit References actions
      addCreditReference: () => {
        const currentRefs = get().creditReferences;
        const newRef: CreditReference = {
          companyName: '',
          contactName: '',
          phone: '',
          accountNumber: '',
          creditLimit: 0,
          currentBalance: 0,
        };
        set({ creditReferences: [...currentRefs, newRef] });
      },

      removeCreditReference: (index) => {
        const currentRefs = get().creditReferences;
        set({
          creditReferences: currentRefs.filter((_, i) => i !== index),
        });
      },

      updateCreditReference: (index, data) =>
        set((state) => ({
          creditReferences: state.creditReferences.map((ref, i) =>
            i === index ? { ...ref, ...data } : ref
          ),
        })),

      // Project Information actions
      setProjectInformation: (data) =>
        set((state) => ({
          projectInformation: {
            ...state.projectInformation,
            ...data,
          },
        })),

      // Credit References Data actions
      setCreditReferencesData: (data) =>
        set((state) => ({
          creditReferencesData: {
            ...state.creditReferencesData,
            ...data,
          },
        })),

      updateSupplierReference: (supplierType, data) =>
        set((state) => ({
          creditReferencesData: {
            ...state.creditReferencesData,
            [supplierType]: {
              ...state.creditReferencesData[supplierType],
              ...data,
            },
          },
        })),

      // Acknowledgement actions
      setAcknowledgement: (data) =>
        set((state) => ({
          acknowledgement: {
            ...state.acknowledgement,
            ...data,
          },
        })),

      addSignature: () => {
        const currentSignatures = get().acknowledgement.signatures;
        const newSignature: AcknowledgementSignature = { ...initialAcknowledgementSignature };
        set({
          acknowledgement: {
            ...get().acknowledgement,
            signatures: [...currentSignatures, newSignature],
          },
        });
      },

      removeSignature: (index) => {
        const currentSignatures = get().acknowledgement.signatures;
        if (currentSignatures.length > 1) {
          set({
            acknowledgement: {
              ...get().acknowledgement,
              signatures: currentSignatures.filter((_, i) => i !== index),
            },
          });
        }
      },

      updateSignature: (index, data) =>
        set((state) => ({
          acknowledgement: {
            ...state.acknowledgement,
            signatures: state.acknowledgement.signatures.map((sig, i) =>
              i === index ? { ...sig, ...data } : sig
            ),
          },
        })),

      // Recently Built Homes actions
      addRecentlyBuiltHome: () => {
        const currentHomes = get().recentlyBuiltHomes;
        const newHome: RecentlyBuiltHome = { ...initialRecentlyBuiltHome };
        set({ recentlyBuiltHomes: [...currentHomes, newHome] });
      },

      removeRecentlyBuiltHome: (index) => {
        const currentHomes = get().recentlyBuiltHomes;
        set({
          recentlyBuiltHomes: currentHomes.filter((_, i) => i !== index),
        });
      },

      updateRecentlyBuiltHome: (index, data) =>
        set((state) => ({
          recentlyBuiltHomes: state.recentlyBuiltHomes.map((home, i) =>
            i === index ? { ...home, ...data } : home
          ),
        })),

      // Application ID action
      setApplicationId: (id) => set({ applicationId: id }),

      // Navigation actions
      setCurrentPage: (page) => set({ currentPage: page }),

      markPageCompleted: (page) => {
        const completed = get().completedPages;
        if (!completed.includes(page)) {
          set({ completedPages: [...completed, page] });
        }
      },

      navigateToPage: (page) => {
        set({ currentPage: page });
        // Auto-scroll to top when navigating
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },

      // File upload actions
      setFiles: (type, files) =>
        set((state) => ({
          ...state,
          [type]: files,
        })),

      // Reset application — explicitly remove the persisted localStorage key first
      // to guarantee stale data is gone before Zustand writes the fresh initial state
      resetApplication: () => {
        localStorage.removeItem(APPLICATION_STORAGE_KEY);
        set(initialLoanApplication);
      },
    }),
    {
      name: APPLICATION_STORAGE_KEY,
      // Only persist specific fields to avoid storing files
      partialize: (state) => ({
        borrowerType: state.borrowerType,
        buildType: state.buildType,
        builtFiveIn3Years: state.builtFiveIn3Years,
        newBorrowerNoReason: state.newBorrowerNoReason,
        experiencedDetail: state.experiencedDetail,
        isOwnedByCompany: state.isOwnedByCompany,
        parentCompanyName: state.parentCompanyName,
        companyInformation: state.companyInformation,
        owners: state.owners,
        disclosureQuestions: {
          bankruptcy: state.disclosureQuestions.bankruptcy,
          legalActions: state.disclosureQuestions.legalActions,
          judgements: state.disclosureQuestions.judgements,
          unpaidTaxes: state.disclosureQuestions.unpaidTaxes,
        },
        builderExperience: state.builderExperience,
        creditReferences: state.creditReferences,
        creditReferencesData: state.creditReferencesData,
        projectInformation: state.projectInformation,
        acknowledgement: {
          signatures: state.acknowledgement.signatures,
          hearAboutUs: state.acknowledgement.hearAboutUs,
          hearAboutUsDetail: state.acknowledgement.hearAboutUsDetail,
        },
        recentlyBuiltHomes: state.recentlyBuiltHomes.map((home) => ({
          customerName: home.customerName,
          customerPhone: home.customerPhone,
          address: home.address,
          projectAmount: home.projectAmount,
          dateCompleted: home.dateCompleted,
          // Don't persist files
        })),
        applicationId: state.applicationId,
        currentPage: state.currentPage,
        completedPages: state.completedPages,
      }),
    }
  )
);
